/* eslint-disable @typescript-eslint/no-unused-vars */
import { FeTileLayoutElement } from '../types/FeTileLayoutElement';
import { Utils } from "../utils/utils";
import DragDropDragInOptions from "../interfaces/DragDropDragInOptions";
import DragDropElementHost from "../interfaces/DragDropElementHost";
import ItemHTMLElement from "../interfaces/ItemHTMLElement";
import DragDropElement from "./DragDropElement";
import DragDropManager from "./DragDropManager";
import { DragAndDropDropOptions } from "../types/DragAndDropDropOptions";
import { DragDropCallback } from "../types/DragDropCallback";
import { DragDropKey } from "../types/DragDropKey";
import { DragDropOptions } from "../types/DragDropOptions";
import { NumberOrString } from '../types/NumberOrString';

/**
 * HTML Native Mouse and Touch Events Drag and Drop functionality.
 */
export default class DragDropTileLayout
{
  /**
   * Handles resizable functionality setup for an element.
   * @param el The element to make resizable.
   * @param opts The options for the resizable functionality.
   * @param key The key to set in the options.
   * @param value The value to set in the options.
   * @returns The current instance of the DragDropTileLayout.
   */
  public resizable (el: ItemHTMLElement, opts: DragDropOptions, key?: DragDropKey, value?: NumberOrString): DragDropTileLayout
  {
    this._getDDElements(el).forEach(dEl =>
    {
      if (opts === 'disable' || opts === 'enable')
      {
        dEl.ddResizable && dEl.ddResizable[ opts ](); // can't create DD as it requires options for setupResizable()
      } else if (opts === 'destroy')
      {
        dEl.ddResizable && dEl.cleanResizable();
      } else if (opts === 'option')
      {
        dEl.setupResizable({ [ key ]: value });
      } else
      {
        const n = dEl.el.tileLayoutNode;
        const tileLayout = n.tileLayout;
        let handles = dEl.el.getAttribute('fe-tl-resize-handles') || tileLayout.opts.resizable.handles || 'e,s,se';
        if (handles === 'all')
          handles = 'n,e,s,w,se,sw,ne,nw';

        const autoHide = !tileLayout.opts.alwaysShowResizeHandle;
        dEl.setupResizable({
          ...tileLayout.opts.resizable,
          ...{ handles, autoHide },
          ...{
            start: opts.start,
            stop: opts.stop,
            resize: opts.resize
          }
        });
      }
    });

    return this;
  }

  /**
   * Handles draggable functionality setup for an element.
   * @param el The element to make draggable.
   * @param opts The options for the draggable functionality.
   * @param key The key to set in the options.
   * @param value The value to set in the options.
   * @returns The current instance of the DragDropTileLayout.
   */
  public draggable (el: ItemHTMLElement, opts: DragDropOptions, key?: DragDropKey, value?: NumberOrString): DragDropTileLayout
  {
    this._getDDElements(el).forEach(dEl =>
    {
      if (opts === 'disable' || opts === 'enable')
      {
        dEl.ddDraggable && dEl.ddDraggable[ opts ](); // can't create DD as it requires options for setupDraggable()
      } else if (opts === 'destroy')
      {
        dEl.ddDraggable && dEl.cleanDraggable();
      } else if (opts === 'option')
      {
        dEl.setupDraggable({ [ key ]: value });
      } else
      {
        const tileLayout = dEl.el.tileLayoutNode.tileLayout;
        dEl.setupDraggable({
          ...tileLayout.opts.draggable,
          ...{
            start: opts.start,
            stop: opts.stop,
            drag: opts.drag
          }
        });
      }
    });
    return this;
  }

  /**
   * Handles drag in functionality setup for an element.
   * @param el The element to make draggable.
   * @param opts The options for the drag in functionality.
   * @returns The current instance of the DragDropTileLayout.
   */
  public dragIn (el: FeTileLayoutElement, opts: DragDropDragInOptions): DragDropTileLayout
  {
    this._getDDElements(el).forEach(dEl => dEl.setupDraggable(opts));
    return this;
  }

  /**
   * Handles droppable functionality setup for an element.
   * @param el The element to make droppable.
   * @param opts The options for the droppable functionality.
   * @param key The key to set in the options.
   * @param value The value to set in the options.
   * @returns The current instance of the DragDropTileLayout.
   */
  public droppable (el: ItemHTMLElement, opts: DragDropOptions | DragAndDropDropOptions, key?: DragDropKey, value?: NumberOrString): DragDropTileLayout
  {
    if (typeof opts.accept === 'function' && !opts._accept)
    {
      opts._accept = opts.accept;
      opts.accept = (el) => opts._accept(el);
    }
    
    this._getDDElements(el).forEach(dEl =>
    {
      if (opts === 'disable' || opts === 'enable')
      {
        dEl.ddDroppable && dEl.ddDroppable[ opts ]();
      } else if (opts === 'destroy')
      {
        if (dEl.ddDroppable)
        { // error to call destroy if not there
          dEl.cleanDroppable();
        }
      } else if (opts === 'option')
      {
        dEl.setupDroppable({ [ key ]: value });
      } else
      {
        dEl.setupDroppable(opts);
      }
    });
    return this;
  }

  /** 
   * Checks if element is droppable 
   * @param el The element to check.
   * @returns True if the element is droppable.
   */
  public isDroppable (el: DragDropElementHost): boolean
  {
    return !!(el?.ddElement?.ddDroppable && !el.ddElement.ddDroppable.disabled);
  }

  /**
   * Checks if element is draggable.
   * @param el The element to check.
   * @returns True if the element is draggable.
   */
  public isDraggable (el: DragDropElementHost): boolean
  {
    return !!(el?.ddElement?.ddDraggable && !el.ddElement.ddDraggable.disabled);
  }

  /**
   * Checks if element is resizable.
   * @param el The element to check.
   * @returns True if the element is resizable
   */
  public isResizable (el: DragDropElementHost): boolean
  {
    return !!(el?.ddElement?.ddResizable && !el.ddElement.ddResizable.disabled);
  }

  /**
   * On event
   * @param el The element to attach the event to.
   * @param name The event name.
   * @param callback The callback function.
   * @returns The current instance of the DragDropTileLayout.
   */
  public on (el: ItemHTMLElement, name: string, callback: DragDropCallback): DragDropTileLayout
  {
    this._getDDElements(el).forEach(dEl => dEl.on(name, (event: Event) =>
    {
      callback(
        event,
        DragDropManager.dragElement ? DragDropManager.dragElement.el : event.target as ItemHTMLElement,
        DragDropManager.dragElement ? DragDropManager.dragElement.helper : null);
    })
    );
    return this;
  }

  /**
   * Off event
   * @param el The element to detach the event from.
   * @param name The event name.
   * @returns The current instance of the DragDropTileLayout.
   */
  public off (el: ItemHTMLElement, name: string): DragDropTileLayout
  {
    this._getDDElements(el).forEach(dEl => dEl.off(name));
    return this;
  }

  /** 
   * @internal 
   * Returns a list of DD elements, creating them on the fly by default 
   * @param els The elements to get the DD elements from.
   * @param create Whether to create the DD elements if they don't exist.
   * @returns The list of DD elements.
   */
  protected _getDDElements (els: FeTileLayoutElement, create = true): DragDropElement[]
  {
    let hosts = Utils.getElements(els) as DragDropElementHost[];
    if (!hosts.length) 
      return [];

    let list = hosts.map(e => e.ddElement || (create ? DragDropElement.init(e) : null));
    // remove nulls
    if (!create) { 
      list.filter(d => d); 
    } 
    return list;
  }
}
